import { useState, useEffect, useCallback } from 'react';
import {
  MessageSquare, Plus, ChevronDown, ChevronRight, Clock,
  CheckCircle2, User, Users, Flag, Send, ArrowLeft, X, Trash2
} from 'lucide-react';

const API = `${getApiUrl()}/api`;
import getApiUrl from '../../../utils/getApiUrl';

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


const PRIORITY_CONFIG = {
  urgent: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Urgent', dot: 'bg-red-500' },
  normal: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Normal', dot: 'bg-blue-500' },
  basse: { bg: 'bg-slate-500/20', text: 'text-slate-400', label: 'Basse', dot: 'bg-slate-500' }
};

const STATUS_CONFIG = {
  a_faire: { label: 'A faire', bg: 'bg-orange-500/20', text: 'text-orange-400' },
  en_cours: { label: 'En cours', bg: 'bg-blue-500/20', text: 'text-blue-400' },
  terminee: { label: 'Terminee', bg: 'bg-emerald-500/20', text: 'text-emerald-400' }
};

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return "a l'instant";
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

// ─── Task Detail Inline ────────────────────────────────────
const TaskDetail = ({ task, onClose, onUpdate, users, userEmail, userName }) => {
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [current, setCurrent] = useState(task);

  const prio = PRIORITY_CONFIG[current.priority] || PRIORITY_CONFIG.normal;
  const stat = STATUS_CONFIG[current.status] || STATUS_CONFIG.a_faire;

  const sendReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      const r = await authFetch(`${API}/tasks/${current.task_id}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: replyText, author_email: userEmail, author_name: userName })
      });
      if (r.ok) {
        const updated = await r.json();
        setCurrent(updated);
        setReplyText('');
        onUpdate(updated);
      }
    } catch (e) { console.error(e); }
    setSending(false);
  };

  const changeStatus = async (newStatus) => {
    try {
      const r = await authFetch(`${API}/tasks/${current.task_id}?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (r.ok) {
        const updated = await r.json();
        setCurrent(updated);
        onUpdate(updated);
      }
    } catch (e) { console.error(e); }
  };

  const threadReplies = (current.thread || []).filter(t => t.type === 'reply');

  return (
    <div className="flex flex-col" data-testid="comm-task-detail">
      <div className="flex items-center gap-2 mb-3">
        <button onClick={onClose} className="p-1 hover:bg-slate-700 rounded" data-testid="comm-detail-back">
          <ArrowLeft className="w-4 h-4 text-slate-400" />
        </button>
        <h4 className="text-sm font-semibold text-white truncate flex-1">{current.title}</h4>
      </div>

      {current.description && (
        <p className="text-xs text-slate-400 mb-3 bg-slate-800/50 rounded-lg p-2 border border-slate-700/50">
          {current.description}
        </p>
      )}

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className={`text-xs px-2 py-0.5 rounded ${prio.bg} ${prio.text}`}>{prio.label}</span>
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
          <button
            key={key}
            onClick={() => changeStatus(key)}
            className={`text-xs px-2 py-0.5 rounded transition-colors ${
              current.status === key
                ? `${cfg.bg} ${cfg.text} ring-1 ring-current`
                : 'bg-slate-800 text-slate-500 hover:text-slate-300'
            }`}
            data-testid={`comm-status-${key}`}
          >
            {cfg.label}
          </button>
        ))}
      </div>

      {current.client_name && (
        <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
          <User className="w-3 h-3" /> {current.client_name}
        </p>
      )}

      {/* Thread */}
      <div className="flex-1 overflow-y-auto max-h-40 space-y-1.5 mb-3">
        {threadReplies.length === 0 && (
          <p className="text-xs text-slate-600 text-center py-3">Aucun message</p>
        )}
        {threadReplies.map((entry, idx) => (
          <div key={idx} className="bg-slate-800/50 rounded-lg p-2 border border-slate-700/50">
            <div className="flex items-center justify-between mb-0.5">
              <span className="text-white font-medium text-xs">{entry.author_name || entry.author?.split('@')[0]}</span>
              <span className="text-slate-600 text-xs">{timeAgo(entry.date)}</span>
            </div>
            <p className="text-xs text-slate-300">{entry.text}</p>
          </div>
        ))}
      </div>

      {/* Reply */}
      <div className="flex gap-2">
        <input
          data-testid="comm-reply-input"
          value={replyText}
          onChange={e => setReplyText(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendReply()}
          className="flex-1 p-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-xs placeholder-slate-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
          placeholder="Repondre..."
        />
        <button
          data-testid="comm-reply-send"
          onClick={sendReply}
          disabled={sending || !replyText.trim()}
          className="px-3 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 rounded-lg text-white"
        >
          <Send className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

// ─── Quick Create Form ─────────────────────────────────────
const QuickCreateForm = ({ onClose, onCreate, users, userEmail, userName }) => {
  const [form, setForm] = useState({ title: '', priority: 'normal', assigned_to: [] });
  const [saving, setSaving] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  const toggleAssignee = (email) => {
    setForm(prev => ({
      ...prev,
      assigned_to: prev.assigned_to.includes(email)
        ? prev.assigned_to.filter(e => e !== email)
        : [...prev.assigned_to, email]
    }));
  };

  const submit = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const r = await authFetch(`${API}/tasks?user_email=${encodeURIComponent(userEmail)}&user_name=${encodeURIComponent(userName || '')}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, description: '', visibility: 'org' })
      });
      if (r.ok) {
        onCreate();
        onClose();
      }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  return (
    <div className="space-y-3" data-testid="comm-quick-create">
      <input
        data-testid="comm-create-title"
        value={form.title}
        onChange={e => setForm({ ...form, title: e.target.value })}
        className="w-full p-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
        placeholder="Titre de la tache..."
        autoFocus
      />
      <div className="flex gap-2">
        <select
          data-testid="comm-create-priority"
          value={form.priority}
          onChange={e => setForm({ ...form, priority: e.target.value })}
          className="flex-1 p-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm"
        >
          <option value="basse">Basse</option>
          <option value="normal">Normal</option>
          <option value="urgent">Urgent</option>
        </select>
      </div>

      {/* Assignees */}
      <div>
        <div
          className="p-2 bg-slate-800 border border-slate-700 rounded-lg cursor-pointer min-h-[36px] flex flex-wrap gap-1 items-center"
          onClick={() => setShowPicker(!showPicker)}
          data-testid="comm-create-assignees"
        >
          {form.assigned_to.length === 0 && <span className="text-slate-500 text-xs">Assigner a...</span>}
          {form.assigned_to.map(email => {
            const u = users.find(u => u.email === email);
            return (
              <span key={email} className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs flex items-center gap-1">
                {u?.name || email.split('@')[0]}
                <X className="w-2.5 h-2.5 cursor-pointer hover:text-white" onClick={e => { e.stopPropagation(); toggleAssignee(email); }} />
              </span>
            );
          })}
        </div>
        {showPicker && (
          <div className="mt-1 bg-slate-800 border border-slate-700 rounded-lg max-h-32 overflow-y-auto">
            {users.map(u => (
              <button
                key={u.email}
                onClick={() => toggleAssignee(u.email)}
                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-slate-700 flex items-center justify-between ${
                  form.assigned_to.includes(u.email) ? 'bg-blue-500/10 text-blue-400' : 'text-slate-300'
                }`}
              >
                <span>{u.name}</span>
                {form.assigned_to.includes(u.email) && <CheckCircle2 className="w-3 h-3" />}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <button onClick={onClose} className="flex-1 px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white text-xs">
          Annuler
        </button>
        <button
          data-testid="comm-create-submit"
          onClick={submit}
          disabled={saving || !form.title.trim()}
          className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 rounded-lg text-white text-xs font-medium"
        >
          {saving ? '...' : 'Creer'}
        </button>
      </div>
    </div>
  );
};

// ─── Main Widget ───────────────────────────────────────────
export default function CommunicationInterneWidget({ authUser }) {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  const userEmail = authUser?.email || '';
  const userName = authUser?.name || '';

  const loadTasks = useCallback(async () => {
    if (!userEmail) return;
    setLoading(true);
    try {
      const r = await authFetch(`${API}/tasks?assigned_to=${encodeURIComponent(userEmail)}&include_created=true&limit=20`);
      if (r.ok) {
        const d = await r.json();
        setTasks(d.tasks || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [userEmail]);

  const loadUsers = useCallback(async () => {
    try {
      const r = await authFetch(`${API}/users/active`);
      if (r.ok) {
        const d = await r.json();
        setUsers(d.users || []);
      }
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadTasks(); loadUsers(); }, [loadTasks, loadUsers]);

  // Auto-refresh every 60s
  useEffect(() => {
    const interval = setInterval(loadTasks, 60000);
    return () => clearInterval(interval);
  }, [loadTasks]);

  const activeTasks = tasks.filter(t => t.status !== 'terminee');
  const unreadCount = tasks.filter(t => t.status !== 'terminee' && !t.read_by?.includes(userEmail)).length;

  const markRead = async (taskId) => {
    await authFetch(`${API}/tasks/${taskId}/read?user_email=${encodeURIComponent(userEmail)}`, { method: 'POST' }).catch(() => {});
  };

  const openTask = (task) => {
    markRead(task.task_id);
    setSelectedTask(task);
    setShowCreate(false);
  };

  const handleTaskUpdated = (updated) => {
    setTasks(prev => prev.map(t => t.task_id === updated.task_id ? updated : t));
    if (selectedTask?.task_id === updated.task_id) setSelectedTask(updated);
  };

  return (
    <div className="bg-slate-800/80 border border-slate-700 rounded-xl overflow-hidden" data-testid="comm-interne-widget">
      {/* Header */}
      <button
        onClick={() => { setIsExpanded(!isExpanded); setSelectedTask(null); setShowCreate(false); }}
        className="w-full flex items-center justify-between p-3 hover:bg-slate-700/50 transition-colors"
        data-testid="comm-widget-toggle"
      >
        <div className="flex items-center gap-2">
          {isExpanded
            ? <ChevronDown className="w-4 h-4 text-slate-400" />
            : <ChevronRight className="w-4 h-4 text-slate-400" />
          }
          <MessageSquare className="w-4 h-4 text-purple-400" />
          <span className="text-sm font-semibold text-white">Communication interne</span>
          {unreadCount > 0 && (
            <span className="ml-1 px-1.5 py-0.5 bg-purple-500 text-white text-xs font-bold rounded-full min-w-[20px] text-center" data-testid="comm-unread-badge">
              {unreadCount}
            </span>
          )}
        </div>
        <span className="text-xs text-slate-500">{activeTasks.length} active{activeTasks.length > 1 ? 's' : ''}</span>
      </button>

      {/* Body */}
      {isExpanded && (
        <div className="px-3 pb-3">
          {/* Selected task detail */}
          {selectedTask ? (
            <TaskDetail
              task={selectedTask}
              onClose={() => setSelectedTask(null)}
              onUpdate={handleTaskUpdated}
              users={users}
              userEmail={userEmail}
              userName={userName}
            />
          ) : showCreate ? (
            <QuickCreateForm
              onClose={() => setShowCreate(false)}
              onCreate={loadTasks}
              users={users}
              userEmail={userEmail}
              userName={userName}
            />
          ) : (
            <>
              {/* Quick add button */}
              <button
                data-testid="comm-add-btn"
                onClick={() => setShowCreate(true)}
                className="w-full mb-3 px-3 py-2 bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/30 rounded-lg flex items-center justify-center gap-2 text-purple-400 text-xs font-medium transition-colors"
              >
                <Plus className="w-3.5 h-3.5" /> Nouvelle tache
              </button>

              {/* Tasks list */}
              {loading ? (
                <div className="flex justify-center py-4">
                  <div className="w-5 h-5 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : activeTasks.length === 0 ? (
                <p className="text-xs text-slate-500 text-center py-4">Aucune tache en cours</p>
              ) : (
                <div className="space-y-1.5 max-h-64 overflow-y-auto">
                  {activeTasks.map(task => {
                    const prio = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.normal;
                    const stat = STATUS_CONFIG[task.status] || STATUS_CONFIG.a_faire;
                    const isUnread = !task.read_by?.includes(userEmail);
                    const threadCount = (task.thread || []).filter(t => t.type === 'reply').length;

                    return (
                      <div
                        key={task.task_id}
                        data-testid={`comm-task-${task.task_id}`}
                        onClick={() => openTask(task)}
                        className={`p-2.5 rounded-lg border cursor-pointer transition-all hover:border-purple-500/40 ${
                          isUnread
                            ? 'bg-purple-500/5 border-purple-500/20'
                            : 'bg-slate-900/40 border-slate-700/50'
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${isUnread ? 'bg-purple-400' : prio.dot}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-white truncate">{task.title}</p>
                            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                              <span className={`text-xs px-1.5 py-0.5 rounded ${stat.bg} ${stat.text}`}>{stat.label}</span>
                              {task.priority === 'urgent' && (
                                <span className={`text-xs px-1.5 py-0.5 rounded ${prio.bg} ${prio.text}`}>{prio.label}</span>
                              )}
                              {task.client_name && (
                                <span className="text-xs text-slate-500 truncate max-w-[80px]">
                                  <User className="w-2.5 h-2.5 inline mr-0.5" />{task.client_name}
                                </span>
                              )}
                              {threadCount > 0 && (
                                <span className="text-xs text-slate-500">
                                  <MessageSquare className="w-2.5 h-2.5 inline mr-0.5" />{threadCount}
                                </span>
                              )}
                            </div>
                          </div>
                          <span className="text-xs text-slate-600 shrink-0">{timeAgo(task.updated_at)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
